/**
 * 遊戲主狀態
 * 管理遊戲的主要邏輯，包括蟑螂生成、點擊處理、UI更新等
 */
import Cockroach from "../prefabs/Cockroach.js";
import CCU from "../prefabs/CCU.js";
import Spider from "../prefabs/Spider.js";
import { GameConfig } from "../config/gameConfig.js";

export default class Game extends Phaser.State {

  /**
   * 初始化遊戲狀態
   */
  constructor() {
    super();
  }

  /**
   * 創建遊戲場景
   * 初始化背景、粒子效果、遊戲物件和UI
   */
  create() {
    // 播放背景音樂
    // this.game.sound.play('On_the_Bach', 1, true);

    // 設置紋理優先級以優化渲染效能
    this.game.renderer.setTexturePriority([
      'cloud_bg', 'cockroach-green', 'cockroach-lbrown',
      'cockroach-purple', 'cockroach-red', 'cockroach-die', 'smoke', 'spider'
    ]);

    // 設置背景
    this.setupBackground();

    // 設置生成機率
    this.spawnChance = GameConfig.SPAWN_CHANCE;

    // 設置UI
    this.setupUI();

    // 設置煙霧粒子效果
    this.setupSmokeEmitter();

    // 初始化遊戲物件群組
    this.cockroaches = this.add.group();
    this.spiders = this.add.group();

    // 定期生成蜘蛛
    this.setupSpiderSpawn();

    // 設置飛行的文字動畫
    this.setupFlyingThoughts();
  }

  /**
   * 設置背景圖層和動畫
   */
  setupBackground() {
    const { WIDTH, HEIGHT, FADE_DURATION } = GameConfig.BACKGROUND;
    this.bg = this.add.tileSprite(0, 0, WIDTH, HEIGHT, 'cloud_bg');
    this.bg.alpha = 0;
    this.game.add.tween(this.bg).to(
      { alpha: 1 },
      FADE_DURATION,
      Phaser.Easing.Linear.None,
      true,
      0
    );
  }

  /**
   * 設置煙霧粒子發射器
   * 創建從底部向上飄散的煙霧效果
   */
  setupSmokeEmitter() {
    const smoke = GameConfig.SMOKE;
    const emitter = this.game.add.emitter(
      this.game.world.centerX,
      this.game.height,
      smoke.PARTICLE_COUNT
    );
    emitter.width = this.game.width;

    // 設置粒子行為參數
    emitter.minParticleScale = smoke.MIN_SCALE;
    emitter.maxParticleScale = smoke.MAX_SCALE;
    emitter.minRotation = smoke.MIN_ROTATION;
    emitter.maxRotation = smoke.MAX_ROTATION;
    emitter.setYSpeed(smoke.Y_SPEED_MAX, smoke.Y_SPEED_MIN);
    emitter.setXSpeed(smoke.X_SPEED_MIN, smoke.X_SPEED_MAX);
    emitter.gravity = smoke.GRAVITY;

    // 設置透明度動畫（淡入淡出效果）
    emitter.setAlpha(0, smoke.ALPHA_MAX, smoke.LIFECYCLE, Phaser.Easing.Quadratic.InOut, true);

    // 啟動發射器
    emitter.makeParticles('smoke');
    emitter.start(false, smoke.LIFECYCLE, smoke.EMIT_INTERVAL, 0);
  }

  /**
   * 設置蜘蛛生成定時器
   */
  setupSpiderSpawn() {
    const { SPAWN_INTERVAL, SPAWN_COUNT, SPAWN_OFFSET_Y } = GameConfig.SPIDER;
    this.game.time.events.repeat(
      Phaser.Timer.SECOND * (SPAWN_INTERVAL / 1000),
      SPAWN_COUNT,
      () => {
        const spider = new Spider(
          this.game,
          Math.random() * this.game.width,
          this.game.height + SPAWN_OFFSET_Y
        );
        this.spiders.add(spider);
      },
      this
    );
  }

  /**
   * 設置飛行文字動畫
   */
  setupFlyingThoughts() {
    const thoughts = [
      '歡迎來到光輝咖碼！',
      '專注於網站架設、網站生成',
      '提供實用教學文章、工具介紹和技術分享',
      '幫助讀者建立和管理自己的網站',
      '從零開始學習如何建立自己的網站',
      'WordPress 使用技巧、外掛介紹、主題設定',
      '靜態網站生成器：Eleventy 等工具介紹',
      '程式開發、程式語言教學',
      '實用的開發工具和生產力工具介紹',
      '網站優化、效能優化、安全性等相關主題',
      '建立自己的網站和線上空間',
      '學習網站開發和管理的技能',
      '了解最新的網站技術趨勢',
      '解決網站架設和開發過程中遇到的問題',
      '為什麼我的網站這麼慢？',
      'Eleventy 怎麼那麼快',
      '靜態網站生成器比較',
      '該使用 PHP 還是 JavaScript 架站？',
      'SEO 優化從這裡開始',
      '效能優化技巧分享',
      '網站安全性很重要',
      '開發工具推薦清單',
      '技術分享工程師日常',
      '這裡有你要的答案 (可能吧😊)',
      '技術交流，一起學習架設網站',
      'BUG 好多啊！！',
      '是在 DEBUG 還是 BUG 產生器',
      'BUG 太多了，我該怎麼辦？',
    ];

    const { INTERVAL } = GameConfig.FLYING_TEXT;
    this.game.time.events.loop(
      Phaser.Timer.SECOND * (INTERVAL / 1000),
      () => {
        const randomIndex = this.game.rnd.integerInRange(0, thoughts.length - 1);
        this.flyThought(thoughts[randomIndex]);
      },
      this
    );
  }


  /**
   * 設置UI層
   * 創建並配置遊戲的用戶界面元素
   */
  setupUI() {
    this.UILayer = this.add.group();
    this.scoreField = new CCU(this.game, "circle", 0);
    this.UILayer.add(this.scoreField);
    this.game.world.bringToTop(this.UILayer);
  }

  /**
   * 遊戲主循環更新
   * 每幀執行，處理背景滾動、蟑螂生成和UI更新
   */
  update() {
    // 確保UI層在最上層
    this.game.world.bringToTop(this.UILayer);

    // 背景滾動
    this.bg.tilePosition.y -= GameConfig.BACKGROUND.SCROLL_SPEED;

    // 嘗試生成新蟑螂
    this.trySpawnCockroach();

    // 更新分數顯示
    this.scoreField.setValue(this.cockroaches.countLiving());
  }

  /**
   * 嘗試生成新蟑螂
   * 根據生成機率決定是否創建新蟑螂，使用物件池優化效能
   */
  trySpawnCockroach() {
    if (Math.random() >= this.spawnChance) {
      return;
    }

    const cockroach = this.cockroaches.getFirstDead();
    const { TYPES, SPAWN_OFFSET_Y, RESET_OFFSET_X, RESET_RANDOM_X } = GameConfig.COCKROACH;

    if (!cockroach) {
      // 創建新蟑螂
      const typeIndex = this.game.rnd.integerInRange(0, TYPES.length - 1);
      const newCockroach = new Cockroach(
        this.game,
        Math.random() * this.game.width,
        this.game.height + SPAWN_OFFSET_Y,
        TYPES[typeIndex]
      );

      newCockroach.anchor.setTo(0.5, 0.5);
      this.setupCockroachClickHandler(newCockroach);
      this.cockroaches.add(newCockroach);
    } else {
      // 復用已死亡的蟑螂（物件池模式）
      cockroach.revive();
      cockroach.reset(
        this.game.width + RESET_OFFSET_X + (Math.random() * RESET_RANDOM_X),
        Math.random() * this.game.height
      );
    }
  }

  /**
   * 設置蟑螂點擊事件處理
   * @param {Phaser.Sprite} cockroach - 要設置點擊事件的蟑螂精靈
   */
  setupCockroachClickHandler(cockroach) {
    cockroach.events.onInputDown.add((sprite) => {
      // 播放隨機音效
      this.playRandomScreamSound();

      // 殺死蟑螂
      cockroach.kill();

      // 顯示死亡動畫
      this.showDeathAnimation(sprite);
    }, this);
  }

  /**
   * 播放隨機尖叫音效
   */
  playRandomScreamSound() {
    const soundIndex = this.game.rnd.integerInRange(0, 3);
    const soundNames = [
      'man-scream-01',
      'man-scream-02',
      'woman-scream-01',
      'woman-scream-02'
    ];
    this.game.sound.play(soundNames[soundIndex]);
  }

  /**
   * 顯示死亡動畫
   * @param {Phaser.Sprite} sprite - 死亡的蟑螂精靈
   */
  showDeathAnimation(sprite) {
    const die = this.game.add.sprite(sprite.x, sprite.y, 'cockroach-die');
    die.anchor.setTo(0.5, 0.5);
    die.width = sprite.width;
    die.height = sprite.height;

    const { FADE_DURATION } = GameConfig.DEATH_ANIMATION;
    const tween = this.game.add.tween(die).to(
      { alpha: 0 },
      FADE_DURATION,
      Phaser.Easing.Linear.None,
      true,
      0,
      0,
      false
    );

    tween.onComplete.add(() => {
      die.destroy();
    }, this);
  }

  /**
   * 創建飛行文字動畫
   * @param {string} txt - 要顯示的文字內容
   */
  flyThought(txt) {
    const { START_X, END_X, MIN_Y, MAX_Y, DURATION, PADDING } = GameConfig.FLYING_TEXT;
    const y = this.game.rnd.integerInRange(MIN_Y, MAX_Y);

    // 創建文字物件（不設置背景色，因為我們會手動創建背景）
    const label = this.game.add.text(0, 0, txt, {
      fill: '#fff',
      fontSize: '32px',
      // font: 'bold 32px Arial'
    });

    // 計算背景尺寸（文字尺寸 + padding）
    const bgWidth = label.width + (PADDING * 2);
    const bgHeight = label.height + (PADDING * 2);

    // 創建背景矩形
    const bg = this.game.add.graphics(0, 0);
    bg.beginFill(0x000000, 1); // 黑色背景，完全不透明
    bg.drawRect(0, 0, bgWidth, bgHeight);
    bg.endFill();

    // 創建一個 Group 來包含背景和文字，方便一起移動
    const textGroup = this.game.add.group();
    textGroup.add(bg);
    textGroup.add(label);

    // 調整文字位置，使其在背景中央（實現 padding 效果）
    label.x = PADDING;
    label.y = PADDING;

    // 設置整個 Group 的位置
    textGroup.x = START_X;
    textGroup.y = y;

    // 對整個 Group 進行動畫
    this.game.add.tween(textGroup).to(
      { x: END_X },
      DURATION,
      Phaser.Easing.Linear.None,
      true,
      0
    );
  }

}
